import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";
import { api, type PagedResult } from "./api";

/**
 * The state a list screen needs: page, search, sort and filters, with search
 * debounced so typing does not fire a request per keystroke.
 */
export interface ListState {
  page: number;
  setPage: (page: number) => void;
  search: string;
  setSearch: (value: string) => void;
  debouncedSearch: string;
  sortBy: string | undefined;
  descending: boolean;
  setSort: (by: string, descending: boolean) => void;
  filters: Record<string, string>;
  setFilter: (key: string, value: string) => void;
  query: Record<string, unknown>;
}

export function useListState(
  options: { pageSize?: number; sortBy?: string; descending?: boolean } = {},
): ListState {
  const { pageSize = 25, sortBy: initialSort, descending: initialDesc = false } = options;

  const [page, setPage] = useState(1);
  const [search, setSearchValue] = useState("");
  const [debouncedSearch, setDebounced] = useState("");
  const [sortBy, setSortBy] = useState<string | undefined>(initialSort);
  const [descending, setDescending] = useState(initialDesc);
  const [filters, setFilters] = useState<Record<string, string>>({});

  useEffect(() => {
    const id = setTimeout(() => setDebounced(search.trim()), 220);
    return () => clearTimeout(id);
  }, [search]);

  // Any change to what is being filtered takes you back to the first page;
  // staying on page 4 of a different result set is never what was meant.
  const setSearch = useCallback((value: string) => {
    setSearchValue(value);
    setPage(1);
  }, []);

  const setFilter = useCallback((key: string, value: string) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  }, []);

  const setSort = useCallback((by: string, desc: boolean) => {
    setSortBy(by);
    setDescending(desc);
    setPage(1);
  }, []);

  const query = useMemo(() => {
    const active: Record<string, unknown> = {
      page,
      pageSize,
      search: debouncedSearch || undefined,
      sortBy,
      // The API's list queries bind SortDescending; any other name is silently ignored.
      sortDescending: descending || undefined,
    };

    for (const [key, value] of Object.entries(filters)) {
      if (value) active[key] = value;
    }

    return active;
  }, [page, pageSize, debouncedSearch, sortBy, descending, filters]);

  return {
    page,
    setPage,
    search,
    setSearch,
    debouncedSearch,
    sortBy,
    descending,
    setSort,
    filters,
    setFilter,
    query,
  };
}

/** A paged GET that follows the list state. */
export function usePagedQuery<T>(
  key: unknown[],
  path: string,
  state: ListState,
  extra: Record<string, unknown> = {},
  options: Partial<UseQueryOptions<PagedResult<T>>> = {},
) {
  return useQuery<PagedResult<T>>({
    queryKey: [...key, state.query, extra],
    queryFn: () => api.get<PagedResult<T>>(path, { ...state.query, ...extra }),
    ...options,
  });
}

/** A plain GET, for detail and overview endpoints. */
export function useResource<T>(
  key: unknown[],
  path: string,
  query?: Record<string, unknown>,
  options: Partial<UseQueryOptions<T>> = {},
) {
  return useQuery<T>({
    queryKey: [...key, query ?? null],
    queryFn: () => api.get<T>(path, query),
    ...options,
  });
}
